import { ResourceTier, RaceId } from '../types/entities.js';
/**
 * All consumable items available in the game. Consumables are non-equipped,
 * single-use items that sit in a dedicated `consumables` inventory bucket
 * (separate from resources and equipment). Buying grants one; using spends
 * one and applies an effect.
 *
 * PvP parity notes:
 *   - Every consumable is purchasable/craftable by every player. No race,
 *     ship class, or faction receives cheaper or stronger variants.
 *   - Repair kits are absolute amounts, not percentages, so a higher-HP
 *     ship doesn't gain a proportionally larger heal.
 *   - Boost multipliers and durations are flat for all users; the buff
 *     tracking lives on the entity so two players consuming the same item
 *     get identical effects regardless of race.
 *
 * Tiering mirrors the existing weapons/shields curve:
 *   - Tier 1: early-game (level 1–5) starter restores.
 *   - Tier 2: mid-tier (level 5–15).
 *   - Tier 3: high-tier (level 15–30).
 *   - Tier 4: end-game (level 30+).
 */
export const CONSUMABLES = {
    // ─── Repair kits (hull) ───
    repair_kit_small: {
        id: 'repair_kit_small',
        name: 'Small Repair Kit', nameEs: 'Kit de Reparación Pequeño',
        description: 'Nano-welders patch a modest 100 HP of hull damage.',
        descriptionEs: 'Nano-soldadores reparan 100 PV de casco.',
        tier: 1, requiredLevel: 1, creditCost: 200,
        craftCost: [{ resource: ResourceTier.ScrapMetal, amount: 15 }],
        category: 'repair',
        effect: { kind: 'repairHull', amount: 100 },
    },
    repair_kit_medium: {
        id: 'repair_kit_medium',
        name: 'Medium Repair Kit', nameEs: 'Kit de Reparación Mediano',
        description: 'Field-grade hull patch kit — restores 300 HP.',
        descriptionEs: 'Kit de campo — restaura 300 PV de casco.',
        tier: 2, requiredLevel: 5, creditCost: 500,
        craftCost: [
            { resource: ResourceTier.ScrapMetal, amount: 30 },
            { resource: ResourceTier.IronOre, amount: 10 },
        ],
        category: 'repair',
        effect: { kind: 'repairHull', amount: 300 },
    },
    repair_kit_large: {
        id: 'repair_kit_large',
        name: 'Large Repair Kit', nameEs: 'Kit de Reparación Grande',
        description: 'Industrial repair pod — restores 700 HP.',
        descriptionEs: 'Cápsula de reparación industrial — 700 PV.',
        tier: 3, requiredLevel: 15, creditCost: 1500,
        craftCost: [
            { resource: ResourceTier.IronOre, amount: 40 },
            { resource: ResourceTier.Silicate, amount: 20 },
        ],
        category: 'repair',
        effect: { kind: 'repairHull', amount: 700 },
    },
    repair_kit_epic: {
        id: 'repair_kit_epic',
        name: 'Nanite Repair Swarm', nameEs: 'Enjambre de Nanitos',
        description: 'Self-replicating nanites rebuild 2000 HP of hull.',
        descriptionEs: 'Nanitos autorreplicantes reconstruyen 2000 PV.',
        tier: 4, requiredLevel: 30, creditCost: 5000,
        craftCost: [
            { resource: ResourceTier.Silicate, amount: 60 },
            { resource: ResourceTier.TitaniumAlloy, amount: 30 },
        ],
        category: 'repair',
        effect: { kind: 'repairHull', amount: 2000 },
    },
    // ─── Repair kits (shield) ───
    shield_battery_small: {
        id: 'shield_battery_small',
        name: 'Small Shield Battery', nameEs: 'Batería de Escudo Pequeña',
        description: 'Emergency shield recharge — +100 shield.',
        descriptionEs: 'Recarga de emergencia — +100 escudo.',
        tier: 1, requiredLevel: 1, creditCost: 250,
        craftCost: [{ resource: ResourceTier.Silicate, amount: 15 }],
        category: 'repair',
        effect: { kind: 'repairShield', amount: 100 },
    },
    shield_battery_medium: {
        id: 'shield_battery_medium',
        name: 'Medium Shield Battery', nameEs: 'Batería de Escudo Mediana',
        description: 'High-density cell — +300 shield.',
        descriptionEs: 'Célula de alta densidad — +300 escudo.',
        tier: 2, requiredLevel: 5, creditCost: 650,
        craftCost: [
            { resource: ResourceTier.Silicate, amount: 30 },
            { resource: ResourceTier.IronOre, amount: 10 },
        ],
        category: 'repair',
        effect: { kind: 'repairShield', amount: 300 },
    },
    shield_battery_large: {
        id: 'shield_battery_large',
        name: 'Large Shield Battery', nameEs: 'Batería de Escudo Grande',
        description: 'Capacitor bank — +700 shield.',
        descriptionEs: 'Banco de capacitores — +700 escudo.',
        tier: 3, requiredLevel: 15, creditCost: 1800,
        craftCost: [
            { resource: ResourceTier.Silicate, amount: 50 },
            { resource: ResourceTier.TitaniumAlloy, amount: 15 },
        ],
        category: 'repair',
        effect: { kind: 'repairShield', amount: 700 },
    },
    // ─── Boosts (thrust) ───
    thrust_booster_short: {
        id: 'thrust_booster_short',
        name: 'Thrust Booster', nameEs: 'Propulsor Turbo',
        description: '+50% thrust for 15 seconds.',
        descriptionEs: '+50% de empuje durante 15 segundos.',
        tier: 1, requiredLevel: 3, creditCost: 400,
        craftCost: [{ resource: ResourceTier.Silicate, amount: 20 }],
        category: 'boost',
        effect: { kind: 'boostThrust', multiplier: 1.5, durationMs: 15_000 },
    },
    thrust_booster_long: {
        id: 'thrust_booster_long',
        name: 'Extended Thrust Booster', nameEs: 'Propulsor Turbo Extendido',
        description: '+50% thrust for 60 seconds.',
        descriptionEs: '+50% de empuje durante 60 segundos.',
        tier: 2, requiredLevel: 10, creditCost: 1200,
        craftCost: [
            { resource: ResourceTier.Silicate, amount: 40 },
            { resource: ResourceTier.IronOre, amount: 15 },
        ],
        category: 'boost',
        effect: { kind: 'boostThrust', multiplier: 1.5, durationMs: 60_000 },
    },
    // ─── Boosts (damage) ───
    damage_booster_short: {
        id: 'damage_booster_short',
        name: 'Combat Stimulant', nameEs: 'Estimulante de Combate',
        description: '+50% weapon damage for 15 seconds.',
        descriptionEs: '+50% de daño durante 15 segundos.',
        tier: 2, requiredLevel: 5, creditCost: 800,
        craftCost: [
            { resource: ResourceTier.IronOre, amount: 20 },
            { resource: ResourceTier.TitaniumAlloy, amount: 5 },
        ],
        category: 'boost',
        effect: { kind: 'boostDamage', multiplier: 1.5, durationMs: 15_000 },
    },
    damage_booster_long: {
        id: 'damage_booster_long',
        name: 'Overclocked Munitions', nameEs: 'Munición Sobrecargada',
        description: '+50% weapon damage for 45 seconds.',
        descriptionEs: '+50% de daño durante 45 segundos.',
        tier: 3, requiredLevel: 20, creditCost: 3000,
        craftCost: [
            { resource: ResourceTier.IronOre, amount: 40 },
            { resource: ResourceTier.TitaniumAlloy, amount: 20 },
        ],
        category: 'boost',
        effect: { kind: 'boostDamage', multiplier: 1.5, durationMs: 45_000 },
    },
    // ─── Race-gated rare consumables ─────────────────────────────────────
    sylphari_regen_pod: {
        id: 'sylphari_regen_pod',
        name: 'Sylphari Regen Pod', nameEs: 'Cápsula Regen Sylphari',
        description: 'Bio-hull patch — restores 1200 HP and seals pressure leaks.',
        descriptionEs: 'Parche biológico — restaura 1200 PV y sella fugas.',
        tier: 3, requiredLevel: 15, creditCost: 2400,
        category: 'repair',
        effect: { kind: 'repairHull', amount: 1200 },
        rarity: 'rare', merchantRaces: [RaceId.Sylphari],
    },
    chronos_chrono_lens: {
        id: 'chronos_chrono_lens',
        name: 'Chrono Lens', nameEs: 'Lente Crónica',
        description: '+50% thrust for 2 full minutes. Time moves differently around you.',
        descriptionEs: '+50% de empuje durante 2 minutos. El tiempo se comporta distinto.',
        tier: 4, requiredLevel: 25, creditCost: 6500,
        category: 'boost',
        effect: { kind: 'boostThrust', multiplier: 1.5, durationMs: 120_000 },
        rarity: 'rare', merchantRaces: [RaceId.ChronosSeers],
    },
    nova_combat_stim: {
        id: 'nova_combat_stim',
        name: 'Nova Combat Stim', nameEs: 'Estimulante Nova',
        description: '+100% weapon damage for 20 seconds. Nova Forger contraband.',
        descriptionEs: '+100% de daño durante 20 segundos. Contrabando Nova.',
        tier: 4, requiredLevel: 35, creditCost: 12000,
        category: 'boost',
        effect: { kind: 'boostDamage', multiplier: 2.0, durationMs: 20_000 },
        rarity: 'rare', merchantRaces: [RaceId.NovaForgers],
    },
    // ─── Common consumable additions ─────────────────────────────────────
    repair_kit_xl: {
        id: 'repair_kit_xl',
        name: 'XL Repair Kit', nameEs: 'Kit de Reparación XL',
        description: 'Heavy-industry repair kit — restores 1950 HP in one go.',
        descriptionEs: 'Kit de reparación industrial — restaura 1950 PV de una vez.',
        tier: 3, requiredLevel: 22, creditCost: 3200,
        craftCost: [
            { resource: ResourceTier.IronOre, amount: 60 },
            { resource: ResourceTier.TitaniumAlloy, amount: 20 },
        ],
        category: 'repair',
        effect: { kind: 'repairHull', amount: 1950 },
    },
    shield_battery_xl: {
        id: 'shield_battery_xl',
        name: 'XL Shield Battery', nameEs: 'Batería de Escudo XL',
        description: 'Industrial capacitor bank — +1500 shield instantly.',
        descriptionEs: 'Banco capacitor industrial — +1500 escudo al instante.',
        tier: 3, requiredLevel: 22, creditCost: 3500,
        craftCost: [
            { resource: ResourceTier.Silicate, amount: 80 },
            { resource: ResourceTier.TitaniumAlloy, amount: 25 },
        ],
        category: 'repair',
        effect: { kind: 'repairShield', amount: 1500 },
    },
    emergency_warp_kit: {
        id: 'emergency_warp_kit',
        name: 'Emergency Warp Kit', nameEs: 'Kit de Salto de Emergencia',
        description: '+80% thrust burst for 8 seconds. Designed to outrun trouble.',
        descriptionEs: '+80% de empuje durante 8 segundos. Diseñado para huir.',
        tier: 2, requiredLevel: 8, creditCost: 1500,
        craftCost: [{ resource: ResourceTier.PlasmaCore, amount: 4 }],
        category: 'boost',
        effect: { kind: 'boostThrust', multiplier: 1.8, durationMs: 8_000 },
    },
    damage_overdrive: {
        id: 'damage_overdrive',
        name: 'Damage Overdrive', nameEs: 'Sobrecarga de Daño',
        description: '+75% weapon damage for 25 seconds. Industrial-grade contraband.',
        descriptionEs: '+75% de daño durante 25 segundos. Contrabando industrial.',
        tier: 3, requiredLevel: 18, creditCost: 2800,
        craftCost: [
            { resource: ResourceTier.PlasmaCore, amount: 6 },
            { resource: ResourceTier.TitaniumAlloy, amount: 12 },
        ],
        category: 'boost',
        effect: { kind: 'boostDamage', multiplier: 1.75, durationMs: 25_000 },
    },
    // ─── Race-gated consumable additions ─────────────────────────────────
    terran_field_kit: {
        id: 'terran_field_kit',
        name: 'Terran Field Kit', nameEs: 'Kit de Campaña Terrano',
        description: 'Terran-issue field repair kit — restores 1800 HP and 500 shield.',
        descriptionEs: 'Kit de campo Terrano — restaura 1800 PV y 500 de escudo.',
        tier: 3, requiredLevel: 18, creditCost: 3800,
        category: 'repair',
        effect: { kind: 'repairHull', amount: 1800 },
        rarity: 'rare', merchantRaces: [RaceId.Terran],
    },
    luminara_focus_lens: {
        id: 'luminara_focus_lens',
        name: 'Luminara Focus Lens', nameEs: 'Lente de Enfoque Luminara',
        description: '+60% thrust for 90 seconds. Luminara crystal-tuned.',
        descriptionEs: '+60% de empuje durante 90 segundos. Sintonizado con cristal Luminara.',
        tier: 3, requiredLevel: 20, creditCost: 4400,
        category: 'boost',
        effect: { kind: 'boostThrust', multiplier: 1.6, durationMs: 90_000 },
        rarity: 'rare', merchantRaces: [RaceId.Luminara],
    },
    vokren_ammo_drum: {
        id: 'vokren_ammo_drum',
        name: 'Vokren Ammo Drum', nameEs: 'Tambor de Munición Vokren',
        description: '+60% weapon damage for 35 seconds. A hidden Vokren drum reload.',
        descriptionEs: '+60% de daño durante 35 segundos. Tambor oculto Vokren.',
        tier: 3, requiredLevel: 22, creditCost: 5200,
        category: 'boost',
        effect: { kind: 'boostDamage', multiplier: 1.6, durationMs: 35_000 },
        rarity: 'rare', merchantRaces: [RaceId.Vokren],
    },
    krelg_battle_stim: {
        id: 'krelg_battle_stim',
        name: 'Krelg Battle Stim', nameEs: 'Estimulante de Combate Krelg',
        description: '+85% weapon damage for 30 seconds. Krelg warlord cocktail.',
        descriptionEs: '+85% de daño durante 30 segundos. Cóctel de señor Krelg.',
        tier: 4, requiredLevel: 32, creditCost: 9500,
        category: 'boost',
        effect: { kind: 'boostDamage', multiplier: 1.85, durationMs: 30_000 },
        rarity: 'rare', merchantRaces: [RaceId.KrelgHegemony],
    },
    sylphari_bloom_pod: {
        id: 'sylphari_bloom_pod',
        name: 'Sylphari Bloom Pod', nameEs: 'Cápsula Floración Sylphari',
        description: '+1900 hull. Sylphari living bio-pod knits flesh and steel alike.',
        descriptionEs: '+1900 PV. Bio-cápsula viva Sylphari, teje carne y metal por igual.',
        tier: 3, requiredLevel: 20, creditCost: 5200,
        category: 'repair',
        effect: { kind: 'repairHull', amount: 1900 },
        rarity: 'rare', merchantRaces: [RaceId.Sylphari],
    },
    chronos_paradox_kit: {
        id: 'chronos_paradox_kit',
        name: 'Chronos Paradox Kit', nameEs: 'Kit Paradoja Crónica',
        description: 'Restores 2500 HP — the Seers say the damage never happened.',
        descriptionEs: 'Restaura 2500 PV — los Videntes dicen que el daño nunca ocurrió.',
        tier: 4, requiredLevel: 30, creditCost: 11000,
        category: 'repair',
        effect: { kind: 'repairHull', amount: 2500 },
        rarity: 'rare', merchantRaces: [RaceId.ChronosSeers],
    },
    // ─── Death insurance (passive, single-use) ───────────────────────────
    //
    // These items have no active "use" — they sit in the consumables bucket
    // and the server burns one stack the instant the carrier dies. They are
    // deliberately expensive: a guaranteed XP/cargo refund is one of the
    // strongest things money can buy in a PvP economy, so the credit cost
    // is sized so a single death-without-insurance is still cheaper than
    // proactively carrying many of them. PvP parity: every race buys the
    // same kit at the same price; nothing on these items modifies damage,
    // HP, speed or fire-rate.
    memory_imprint_chip: {
        id: 'memory_imprint_chip',
        name: 'Memory Imprint Chip', nameEs: 'Chip de Memoria',
        description: 'Premium-only neural backup module. If you die while carrying one, ' +
            'the chip is sacrificed and your XP is preserved in full.',
        descriptionEs: 'Módulo de respaldo neural solo Premium. Si mueres con uno, el chip ' +
            'se sacrifica y conservas toda tu experiencia.',
        tier: 3, requiredLevel: 10, creditCost: 25_000,
        category: 'insurance',
        effect: { kind: 'preserveOnDeath', protect: 'xp' },
        rarity: 'rare',
        // Insurance kits are now Premium-only at PURCHASE — free pilots
        // who already own a stack from before this change keep using
        // them normally (the use pipeline never gates on premium), but
        // re-stocking after a death now requires an active subscription.
        // The Premium Aegis Implant is a strict superset of these (it
        // covers BOTH XP and cargo on a single stack), so a subscriber
        // upgrading their loadout has a clear progression path.
        premiumOnly: true,
    },
    cargo_lockbox: {
        id: 'cargo_lockbox',
        name: 'Cargo Lockbox', nameEs: 'Caja Fuerte de Carga',
        description: 'Premium-only auto-sealing cargo vault. If you die while carrying ' +
            'one, the vault is consumed and every resource and consumable in ' +
            'your hold is kept.',
        descriptionEs: 'Bóveda de carga autoasegurada solo Premium. Si mueres con una, ' +
            'se consume y se conservan todos los recursos y consumibles de tu bodega.',
        tier: 4, requiredLevel: 15, creditCost: 50_000,
        category: 'insurance',
        effect: { kind: 'preserveOnDeath', protect: 'inventory' },
        rarity: 'rare',
        premiumOnly: true,
    },
    // ─── Apex repair / boost line (level 100+) ───────────────────────────
    // Hull / shield amounts here jump in step with apex shield pools and
    // hull HP; keeping repair *amounts* absolute (not %) preserves the
    // PvP-parity rule from the file header.
    repair_kit_legendary: {
        id: 'repair_kit_legendary',
        name: 'Legendary Hull Kit', nameEs: 'Kit Legendario de Casco',
        description: 'Mythic-tier nanite swarm — restores 5000 HP and patches breached hull modules.',
        descriptionEs: 'Enjambre de nanitos mítico — restaura 5000 PV y repara módulos de casco rotos.',
        tier: 5, requiredLevel: 100, creditCost: 60_000,
        craftCost: [
            { resource: ResourceTier.QuantumFragment, amount: 6 },
            { resource: ResourceTier.TitaniumAlloy, amount: 80 },
        ],
        category: 'repair',
        effect: { kind: 'repairHull', amount: 5000 },
        rarity: 'rare',
    },
    shield_battery_legendary: {
        id: 'shield_battery_legendary',
        name: 'Legendary Shield Cell', nameEs: 'Célula Legendaria de Escudo',
        description: 'Apex capacitor — instantly replenishes 4000 shield.',
        descriptionEs: 'Capacitor ápice — reabastece 4000 de escudo al instante.',
        tier: 5, requiredLevel: 100, creditCost: 70_000,
        craftCost: [
            { resource: ResourceTier.QuantumFragment, amount: 5 },
            { resource: ResourceTier.Silicate, amount: 120 },
        ],
        category: 'repair',
        effect: { kind: 'repairShield', amount: 4000 },
        rarity: 'rare',
    },
    thrust_booster_apex: {
        id: 'thrust_booster_apex',
        name: 'Apex Thrust Vial', nameEs: 'Vial de Empuje Ápice',
        description: '+75% thrust for 90 seconds. Distilled from Eidolon mirror-flame.',
        descriptionEs: '+75% de empuje durante 90 segundos. Destilado de fuego espejo Eidolón.',
        tier: 5, requiredLevel: 130, creditCost: 90_000,
        category: 'boost',
        effect: { kind: 'boostThrust', multiplier: 1.75, durationMs: 90_000 },
        rarity: 'rare', merchantRaces: [RaceId.EidolonConclave],
    },
    damage_booster_apex: {
        id: 'damage_booster_apex',
        name: 'Sovereign Wrath', nameEs: 'Ira del Soberano',
        description: '+120% weapon damage for 25 seconds. Sovereign-grade contraband. Do not get caught.',
        descriptionEs: '+120% de daño durante 25 segundos. Contrabando de grado Soberano. Que no te pillen.',
        tier: 6, requiredLevel: 200, creditCost: 220_000,
        category: 'boost',
        effect: { kind: 'boostDamage', multiplier: 2.2, durationMs: 25_000 },
        rarity: 'legendary', merchantRaces: [RaceId.SingularitySovereign],
    },
    leviathan_lullaby: {
        id: 'leviathan_lullaby',
        name: 'Leviathan Lullaby', nameEs: 'Nana de Leviatán',
        description: '+8000 hull. The Astralleviathan hum a piece of their own song into your engineering bay.',
        descriptionEs: '+8000 PV. Los Leviatán Astral cantan parte de su propia canción en tu sala de ingeniería.',
        tier: 6, requiredLevel: 220, creditCost: 320_000,
        category: 'repair',
        effect: { kind: 'repairHull', amount: 8000 },
        rarity: 'legendary', merchantRaces: [RaceId.AstralLeviathan],
    },
    ether_lattice: {
        id: 'ether_lattice',
        name: 'Ether Lattice', nameEs: 'Retícula del Éter',
        description: '+6000 shield. Tier-9 ether scaffolding that knits straight into your shield emitter.',
        descriptionEs: '+6000 de escudo. Andamiaje etéreo de tier 9 que se entrelaza con el emisor.',
        tier: 7, requiredLevel: 320, creditCost: 480_000,
        category: 'repair',
        effect: { kind: 'repairShield', amount: 6000 },
        rarity: 'legendary',
    },
    thrust_overcharge_eternal: {
        id: 'thrust_overcharge_eternal',
        name: 'Eternal Thrust Overcharge', nameEs: 'Sobrecarga Eterna de Empuje',
        description: '+100% thrust for 5 full minutes. Tier-10 contraband. Bring spare engines.',
        descriptionEs: '+100% de empuje durante 5 minutos. Contrabando de tier 10. Trae motores de repuesto.',
        tier: 8, requiredLevel: 500, creditCost: 1_500_000,
        category: 'boost',
        effect: { kind: 'boostThrust', multiplier: 2.0, durationMs: 300_000 },
        rarity: 'legendary',
    },
    infinity_repair_swarm: {
        id: 'infinity_repair_swarm',
        name: 'Infinity Repair Swarm', nameEs: 'Enjambre Reparador Infinito',
        description: '+25 000 hull. Restores even structural damage that other kits cannot reach.',
        descriptionEs: '+25 000 PV. Restaura incluso el daño estructural que otros kits no alcanzan.',
        tier: 9, requiredLevel: 800, creditCost: 6_000_000,
        category: 'repair',
        effect: { kind: 'repairHull', amount: 25_000 },
        rarity: 'legendary',
    },
    // ─── Premium-only line ───────────────────────────────────────────────
    //
    // Three convenience-tier consumables sold ONLY to active Premium
    // subscribers. The server enforces the gate at `BuyItem`; the
    // station-shop snapshot still includes the rows so free pilots see
    // exactly what their subscription would unlock. Effects are
    // intentionally tuned to "very strong utility, zero PvP-only
    // advantage" — every effect kind already exists in the base game,
    // these are just bigger / faster / longer versions of it. A free
    // pilot who acquires one (gift, drop) can use it normally.
    premium_quantum_repair: {
        id: 'premium_quantum_repair',
        name: 'Quantum Repair Cell', nameEs: 'Célula Cuántica de Reparación',
        description: 'Premium-only field kit. Restores 4000 HP and 4000 shield in a single tick. ' +
            'Subscribers only.',
        descriptionEs: 'Kit de campo solo Premium. Restaura 4000 PV y 4000 de escudo en un tic. ' +
            'Solo suscriptores.',
        tier: 4, requiredLevel: 20, creditCost: 8_000,
        // Premium recipes are tuned to be MEANINGFULLY cheaper per
        // healing point than the equivalent free-tier kits, so the
        // perk is "buy or craft for less" rather than "craft to save
        // money on the same item". Roughly 60% the resource cost of a
        // pair of advanced shield/hull patches.
        craftCost: [
            { resource: ResourceTier.TitaniumAlloy, amount: 30 },
            { resource: ResourceTier.PlasmaCore, amount: 8 },
        ],
        category: 'repair',
        // Hull restore is the headline; the shield half is granted by a
        // second consumable usage step the server fans out (see
        // PREMIUM_QUANTUM_REPAIR_ITEM_ID handling in the use pipeline).
        effect: { kind: 'repairHull', amount: 4000 },
        rarity: 'legendary',
        premiumOnly: true,
    },
    premium_overdrive_serum: {
        id: 'premium_overdrive_serum',
        name: 'Overdrive Serum', nameEs: 'Suero de Sobreimpulso',
        description: 'Premium-only thruster overdrive. +60% thrust for 60 seconds. ' +
            'Long enough to outrun a wing of pirates between gates.',
        descriptionEs: 'Sobreimpulso solo Premium. +60% de empuje durante 60 segundos. ' +
            'Suficiente para escapar de una escuadra entre portales.',
        tier: 4, requiredLevel: 15, creditCost: 6_000,
        craftCost: [
            { resource: ResourceTier.PlasmaCore, amount: 12 },
            { resource: ResourceTier.Silicate, amount: 40 },
        ],
        category: 'boost',
        effect: { kind: 'boostThrust', multiplier: 1.6, durationMs: 60_000 },
        rarity: 'legendary',
        premiumOnly: true,
    },
    premium_aegis_implant: {
        id: 'premium_aegis_implant',
        name: 'Aegis Implant', nameEs: 'Implante Aegis',
        description: 'Premium-only neural insurance. Preserves BOTH your XP AND your ' +
            'cargo on the next death — single use, like the standard insurance ' +
            'kits, but combines both protections into one stack.',
        descriptionEs: 'Seguro neuronal solo Premium. Conserva TANTO tu XP COMO tu carga ' +
            'en la próxima muerte — un solo uso, combina ambas protecciones ' +
            'en una sola pila.',
        tier: 5, requiredLevel: 25, creditCost: 60_000,
        // Crafting the Aegis Implant is intentionally expensive — it's a
        // dual insurance kit and the server-side cost of one death
        // wiped by it is significant. Premium pilots can still craft it
        // for ~60% of the buy price using late-tier resources.
        craftCost: [
            { resource: ResourceTier.PlasmaCore, amount: 25 },
            { resource: ResourceTier.TitaniumAlloy, amount: 60 },
        ],
        category: 'insurance',
        // Mechanically uses the existing 'preserveOnDeath' XP variant; the
        // server's death handler also checks for this id specifically and
        // grants the inventory protection in addition. Keeps the wire shape
        // unchanged so older clients don't choke on a new effect kind.
        effect: { kind: 'preserveOnDeath', protect: 'xp' },
        rarity: 'legendary',
        premiumOnly: true,
    },
    premium_starfarer_cache: {
        id: 'premium_starfarer_cache',
        name: 'Starfarer Repair Cache', nameEs: 'Caché de Reparación Estelar',
        description: 'Premium-only mid-game field cache. Restores 8000 HP in a single ' +
            'tick — markedly cheaper per hull point than the free-tier kits.',
        descriptionEs: 'Caché de campo solo Premium. Restaura 8000 PV en un solo tic — ' +
            'notablemente más barato por punto de casco que los kits gratuitos.',
        tier: 5, requiredLevel: 35, creditCost: 18_000,
        craftCost: [
            { resource: ResourceTier.TitaniumAlloy, amount: 45 },
            { resource: ResourceTier.NeutroniumDust, amount: 6 },
        ],
        category: 'repair',
        effect: { kind: 'repairHull', amount: 8000 },
        rarity: 'legendary',
        premiumOnly: true,
    },
    premium_aegis_recharger: {
        id: 'premium_aegis_recharger',
        name: 'Aegis Recharger', nameEs: 'Recargador Égida',
        description: 'Premium-only shield recharger. Instantly restores 8000 shield — ' +
            'keeps deep-sector farming runs going without a station stop.',
        descriptionEs: 'Recargador de escudo solo Premium. Restaura 8000 de escudo al ' +
            'instante — mantiene el farmeo en sectores profundos sin pasar por estación.',
        tier: 5, requiredLevel: 50, creditCost: 24_000,
        craftCost: [
            { resource: ResourceTier.CrystalShards, amount: 60 },
            { resource: ResourceTier.NeutroniumDust, amount: 8 },
        ],
        category: 'repair',
        effect: { kind: 'repairShield', amount: 8000 },
        rarity: 'legendary',
        premiumOnly: true,
    },
};
/**
 * Stable ids for the two death-insurance consumables. The server's death
 * pipeline looks them up by definition id rather than effect kind so we
 * can short-circuit the inventory scan to a couple of map lookups.
 */
export const XP_INSURANCE_ITEM_ID = 'memory_imprint_chip';
export const CARGO_INSURANCE_ITEM_ID = 'cargo_lockbox';
/**
 * Stable ids for the Premium-only consumable line. The hot paths
 * (use pipeline, death handler, station shop gate) reference these
 * constants instead of the literal strings so renaming an item is a
 * one-file change.
 */
export const PREMIUM_QUANTUM_REPAIR_ITEM_ID = 'premium_quantum_repair';
export const PREMIUM_OVERDRIVE_SERUM_ITEM_ID = 'premium_overdrive_serum';
export const PREMIUM_AEGIS_IMPLANT_ITEM_ID = 'premium_aegis_implant';
/**
 * Returns true when the consumable is gated to Premium subscribers
 * for purchase. Drives the station-shop "PREMIUM" badge AND the
 * server-side `BuyItem` rejection. Defined here so the gate logic
 * doesn't have to import the full `CONSUMABLES` table at every
 * call site.
 */
export function isPremiumConsumable(itemId) {
    return CONSUMABLES[itemId]?.premiumOnly === true;
}
export const CONSUMABLES_ORDERED = Object.values(CONSUMABLES)
    .sort((a, b) => a.requiredLevel - b.requiredLevel || a.creditCost - b.creditCost);
export function lookupConsumable(id) {
    return CONSUMABLES[id];
}
/** Inventory key-prefix used for consumables on the wire and in persistence. */
export const CONSUMABLE_ITEM_PREFIX = 'consumable_';
/** Build the persistence/wire id for a consumable from its definition id. */
export function consumableItemId(id) {
    return `${CONSUMABLE_ITEM_PREFIX}${id}`;
}
/** Inverse of {@link consumableItemId}; returns `null` for non-consumable ids. */
export function parseConsumableItemId(itemId) {
    return itemId.startsWith(CONSUMABLE_ITEM_PREFIX)
        ? itemId.slice(CONSUMABLE_ITEM_PREFIX.length)
        : null;
}
//# sourceMappingURL=consumables.js.map